import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, orderBy, onSnapshot, getDocs, doc, deleteDoc, startAfter, endBefore, limit, limitToLast } from 'firebase/firestore';
import EditReservationModal from './EditReservationModal';
import './AdminReservations.css';

const ITEMS_PER_PAGE = 10;

const AdminReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('future'); // future, today, history
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSede, setSelectedSede] = useState('');
  const [lastDoc, setLastDoc] = useState(null);
  const [firstDoc, setFirstDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [reservationCounts, setReservationCounts] = useState({ future: 0, today: 0, history: 0 });

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const reservationsCollection = collection(db, 'reservations');

    const qFuture = query(reservationsCollection, where('date', '>', today));
    const qToday = query(reservationsCollection, where('date', '==', today));
    const qHistory = query(reservationsCollection, where('date', '<', today));

    const unsubFuture = onSnapshot(qFuture, (snapshot) => setReservationCounts(prev => ({ ...prev, future: snapshot.size })));
    const unsubToday = onSnapshot(qToday, (snapshot) => setReservationCounts(prev => ({ ...prev, today: snapshot.size })));
    const unsubHistory = onSnapshot(qHistory, (snapshot) => setReservationCounts(prev => ({ ...prev, history: snapshot.size })));

    return () => {
      unsubFuture();
      unsubToday();
      unsubHistory();
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    let unsubscribe = () => {};

    const today = new Date().toISOString().split('T')[0];

    let queryConstraints = [];

    if (view === 'today') {
      queryConstraints.push(where('date', '==', today));
    } else if (view === 'future') {
      queryConstraints.push(where('date', '>', today));
    } else { // history
      queryConstraints.push(where('date', '<', today));
    }

    if (selectedSede) {
      queryConstraints.push(where('sede', '==', selectedSede));
    }

    if (selectedDate) {
        queryConstraints.push(where('date', '==', selectedDate));
    }

    if (searchTerm) {
        queryConstraints.push(where('name', '>=', searchTerm));
        queryConstraints.push(where('name', '<=', searchTerm + '\uf8ff'));
    }

    const reservationsCollection = collection(db, 'reservations');
    const q = query(reservationsCollection, ...queryConstraints, orderBy('date', 'desc'), orderBy('createdAt', 'desc'));

    unsubscribe = onSnapshot(q, (querySnapshot) => {
      const reservationsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
      }));
      setReservations(reservationsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching real-time reservations:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [view, searchTerm, selectedDate, selectedSede]);

  const handleEdit = (id) => {
    const reservationToEdit = reservations.find(res => res.id === id);
    setSelectedReservation(reservationToEdit);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReservation(null);
  };

  const handleSave = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta reservación?')) {
      try {
        await deleteDoc(doc(db, 'reservations', id));
      } catch (error) {
        console.error('Error deleting reservation:', error);
      }
    }
  };

  if (loading) {
    return <div className="loading-message">Cargando reservaciones...</div>;
  }

  return (
    <div className="admin-reservations-container">
      <h2>Reservaciones</h2>
      <div className="controls-container">
        <div className="filter-buttons">
            <button onClick={() => setView('future')} disabled={view === 'future'}>Futuras ({reservationCounts.future})</button>
            <button onClick={() => setView('today')} disabled={view === 'today'}>Hoy ({reservationCounts.today})</button>
            <button onClick={() => setView('history')} disabled={view === 'history'}>Historial ({reservationCounts.history})</button>
        </div>
        <select className="sede-filter" value={selectedSede} onChange={e => setSelectedSede(e.target.value)}>
          <option value="">Todas las Sedes</option>
          <option value="Marbella">Marbella</option>
          <option value="Bocagrande">Bocagrande</option>
        </select>
        <input
            type="text"
            placeholder="Buscar por nombre..."
            className="search-bar"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
        />
        <input
            type="date"
            className="date-filter"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
        />
      </div>

      {reservations.length > 0 ? (
        <>
          <table className="reservations-table">
            <thead>
              <tr>
                <th>Fecha de Creación</th>
                <th>Sede</th>
                <th>Comensales</th>
                <th>Fecha Reserva</th>
                <th>Hora</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map(res => (
                <tr key={res.id}>
                  <td>{res.createdAt.toLocaleString()}</td>
                  <td>{res.sede}</td>
                  <td>{res.guests}</td>
                  <td>{res.date}</td>
                  <td>{res.time}</td>
                  <td>{res.name}</td>
                  <td>{res.email}</td>
                  <td>
                    <button className="action-button edit-button" onClick={() => handleEdit(res.id)}>Editar</button>
                    <button className="action-button delete-button" onClick={() => handleDelete(res.id)}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <div className="no-results-message">No se encontraron reservaciones.</div>
      )}

      {isModalOpen && (
        <EditReservationModal
          reservation={selectedReservation}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

export default AdminReservations;